import {
	FIELD_HISTORY_IMPORT_EMPLOYEE_COUNT_FIELD,
	type ImportFieldHistoryResponse,
	type ValidateFieldHistoryImportResponse,
} from "@performyard/service-contracts";

import { ErrorResponse, getHeaders, getUrl } from "./internal-base-request";

const BASE = "/v1/field-history-import";

// internal-api is cross-origin and only Fastify's replies carry CORS headers, so an infra response
// rejects the fetch instead of arriving with a readable status — there is nothing to branch on.
const IMPORT_INTERRUPTED_MESSAGE =
	"We didn't get confirmation that the import started. Give it a few minutes before uploading again — a second import is refused while one is in progress.";
const VALIDATE_INTERRUPTED_MESSAGE =
	"We didn't get an answer back while checking your file. Nothing was imported — try again, and if it keeps failing try a smaller file.";

// Raw fetch, not the shared `request` helper: multipart bodies must not get a JSON Content-Type.
const postFieldHistoryCsv = async <T>(
	url: string,
	file: File,
	interruptedMessage: string,
	fields?: Record<string, string>
): Promise<T> => {
	const formData = new FormData();
	// Fields first: the server reads them off the file part, which carries only what was parsed before it.
	for (const [name, value] of Object.entries(fields ?? {})) {
		formData.append(name, value);
	}
	formData.append("file", file);

	// Resolved before the try: a throw from either is a client-state problem, not an interrupted upload.
	const requestUrl = getUrl(url);
	const headers = getHeaders() as Record<string, string>;

	let response: Response;
	try {
		response = await fetch(requestUrl, { method: "POST", headers, body: formData });
	} catch {
		// A dropped connection mid-upload rejects with a bare TypeError, surfacing as "Failed to fetch".
		throw new ErrorResponse(interruptedMessage, new Response(null, { status: 503 }), {
			message: interruptedMessage,
		});
	}

	let data: unknown;
	try {
		data = await response.json();
	} catch {
		if (response.ok) {
			throw new ErrorResponse("Bad fetch response", response, {});
		}
		// Defensive: Fastify's errors are always JSON, and a non-Fastify response can't be read here.
		throw new ErrorResponse(interruptedMessage, response, { message: interruptedMessage });
	}

	if (!response.ok) {
		throw new ErrorResponse(
			(data as { message?: string })?.message || "Bad fetch response",
			response,
			data as object
		);
	}
	return data as T;
};

export const postValidateFieldHistoryCsv = async (file: File): Promise<ValidateFieldHistoryImportResponse> =>
	postFieldHistoryCsv<ValidateFieldHistoryImportResponse>(`${BASE}/validate`, file, VALIDATE_INTERRUPTED_MESSAGE);

/** `employeeCount` comes from this file's validation and seeds the task's progress total. */
export const postImportFieldHistoryCsv = async (
	file: File,
	employeeCount: number
): Promise<ImportFieldHistoryResponse> =>
	postFieldHistoryCsv<ImportFieldHistoryResponse>(`${BASE}/`, file, IMPORT_INTERRUPTED_MESSAGE, {
		[FIELD_HISTORY_IMPORT_EMPLOYEE_COUNT_FIELD]: employeeCount.toString(),
	});
